import { useEffect, useId, useRef, useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import type { RegistrationResponseJSON } from '@simplewebauthn/browser';
import { Fingerprint } from 'lucide-react';
import { toDataURL as qrToDataURL } from 'qrcode';
import { webauthnRegisterOptions, webauthnRegisterVerify, totpEnroll, totpActivate } from '../api/mfa';
import type { EnrollmentStepUp } from '../api/mfa';
import { ApiError, userMessage } from '../api/client';
import { hasVisibleContent } from '@steward-app/steward-types';
import { cls as formCls } from '../components/formStyles';
import { INVISIBLE_NAME_MESSAGE } from '../lib/nameCopy';

const ICON_STROKE = 1.75;

/**
 * The two factor-add forms, shared by {@link EnrollmentWall} (first-run,
 * pre-app) and Settings → Sign-in keys (post-enrollment, ongoing management).
 * Both routes end up calling the same `/me/factors/webauthn/*` and
 * `/me/factors/totp/*` façade passthroughs; a factor added past the first one
 * may trip the MED-2 step-up gate, which the central interceptor in
 * api/client.ts handles transparently — these forms never special-case it.
 *
 * `onDone` receives the enrollment's {@link EnrollmentStepUp} so a caller can
 * tell whether appcore already minted this session's step-up as part of the
 * ceremony (appcore docs/29 F1) and skip asking for a redundant one. Callers that
 * don't care can keep a zero-argument handler.
 */

/** appcore's own cap on a factor label (`handlers/factors.ts`), mirrored here so a
 *  too-long name is stopped by the input rather than by an upstream 400. Shared by
 *  both add-a-factor forms: the bound is appcore's, not per-factor-type. */
const MAX_LABEL_LENGTH = 100;

/** appcore's own bound on the recovery code (`trim + 1..64`), mirrored for the input. */
const MAX_RECOVERY_CODE_LENGTH = 64;

/**
 * The four `400`s appcore's operator-recovery gate raises (appcore docs/29 F29). A
 * reset leaves an account with zero factors, and first-factor enrollment is otherwise
 * exempt from the step-up gate, so appcore holds the account with a one-time code that
 * an operator reads to the member on a verified call. These codes are the ONLY signal
 * that a hold exists: there is deliberately no endpoint that reports one, because such
 * an endpoint is a free hold-detection oracle for a session hijacker.
 *
 * All four are 400, and specifically NOT `403 STEP_UP_REQUIRED`: that code belongs to
 * the step-up interceptor in api/client.ts, which would swallow it and run a passkey
 * ceremony the user cannot complete (they have no factors, which is why they are here).
 * Nor 401, which would sign them out of a perfectly good session.
 */
const RECOVERY_CODE_ERROR_CODES = [
  'RECOVERY_CODE_REQUIRED',
  'RECOVERY_CODE_INVALID',
  'RECOVERY_CODE_EXPIRED',
  'RECOVERY_CODE_EXHAUSTED',
] as const;

type RecoveryCodeError = (typeof RECOVERY_CODE_ERROR_CODES)[number];

/**
 * The two failures whose only remedy is the operator re-running the reset for a fresh
 * code, which matters far beyond the copy: `resetUserFactors` deletes EVERY `FACTOR#` row
 * (including a pending TOTP secret) and EVERY `FACTORCHAL#` row across all of the user's
 * sessions. So the moment an operator issues a fresh code, any half-finished ceremony on
 * this screen is already dead upstream, and anything the form is holding has to be thrown
 * away rather than retried.
 */
const REMEDY_IS_A_FRESH_RESET: readonly RecoveryCodeError[] = [
  'RECOVERY_CODE_EXPIRED',
  'RECOVERY_CODE_EXHAUSTED',
];

/**
 * The recovery error code an enrollment call failed with, or undefined if it failed for
 * some other reason (or is not an {@link ApiError} at all).
 *
 * Checks the STATUS as well as the code, matching `isWorkspaceSuspendedResponse` in
 * api/client.ts, which pins both halves of the structurally identical check for the same
 * reason. appcore only ever raises these four as 400s today, so this is defence in depth:
 * if one ever arrived as a 401 or 403 it would be a session-dead or step-up signal, and
 * rendering it as a recovery prompt would run the wrong ceremony on the wrong problem.
 */
function recoveryCodeErrorOf(err: unknown): RecoveryCodeError | undefined {
  return err instanceof ApiError && err.status === 400
    && (RECOVERY_CODE_ERROR_CODES as readonly string[]).includes(err.code)
    ? err.code as RecoveryCodeError
    : undefined;
}

/** True when appcore says the caller's pending TOTP enrollment is gone. Either the
 *  15 minute pending TTL lapsed, or a reset cascade deleted it. Both mean the secret on
 *  screen is dead and the only way forward is minting a new one. Status-checked for the
 *  same reason as {@link recoveryCodeErrorOf}: a bare code match is looser than it looks. */
function isPendingEnrollmentGone(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404 && err.code === 'FACTOR_NOT_FOUND';
}

/**
 * Fold a code the way appcore does at compare time, for ONE purpose: deciding whether the
 * member has just retyped a value the server already spent an attempt refusing.
 *
 * ⚠️ Never use this for what gets SENT. appcore's `normalizeRecoveryCode` is the only
 * authority on the comparison that decides access, and duplicating it into the payload is
 * how the two drift apart. Here it is only a UX guard, and drifting is harmless in both
 * directions: laxer folding blocks a resubmit it need not, stricter folding lets one
 * through, and neither changes what the server does with the value.
 *
 * Without it the guard is trivially bypassed by shape alone: `ABCDE-FGHJK` refused, then
 * `abcde fghjk` re-arms the button and spends attempt 2 of 5 on the same code.
 */
function foldForCompare(raw: string): string {
  return raw.replace(/[\s-]/g, '').toUpperCase().replace(/[IL]/g, '1').replace(/O/g, '0');
}

/**
 * The recovery-code field's state, shared by both forms. Extracted because both need
 * identical behaviour and one rule here is easy to get subtly wrong twice.
 */
function useRecoveryCode() {
  const [code, setCode] = useState('');
  const [needed, setNeeded] = useState(false);
  /**
   * The last value the server CHARGED AN ATTEMPT for. appcore spends one on `invalid`,
   * `expired` AND `exhausted` (only a merely-absent code is free) and the cap is five, so
   * a member who clicks Continue five times on a code that just expired locks themselves
   * out of their own recovery. Re-sending a value the server has already refused cannot
   * succeed, so it is blocked until they change it. The remedy is never blocked: a freshly
   * issued code is a different string.
   */
  const [rejected, setRejected] = useState<string | null>(null);
  const inputId = useId();
  const trimmed = code.trim();

  return {
    value: code,
    set: setCode,
    needed,
    inputId,
    /** Present only when there is something to send, so a blank never reaches the wire.
     *  The RAW trimmed value: appcore owns the normalization that decides the compare. */
    payload: trimmed ? { recoveryCode: trimmed } : {},
    /** Non-empty, and not something the server already spent an attempt refusing. Compared
     *  folded, because the server folds too: a different SHAPE of a refused code is the same
     *  refused code, and would spend a second attempt to be told so again. */
    submittable: trimmed !== '' && foldForCompare(trimmed) !== rejected,
    /** Stop asking, but KEEP the value: TOTP activation still has to carry it. */
    accepted() { setNeeded(false); setRejected(null); },
    /**
     * Classify a failed call. Returns the recovery error if it was one (and updates state
     * for it), else undefined so the caller can handle its own errors.
     */
    classify(err: unknown, setError: (message: string) => void): RecoveryCodeError | undefined {
      const failure = recoveryCodeErrorOf(err);
      if (!failure) return undefined;
      setNeeded(true);
      if (failure === 'RECOVERY_CODE_REQUIRED') {
        // The server ASKING, not the member getting something wrong. It costs no attempt,
        // and appcore's copy for it in a red box would blame them for a field they have
        // not been offered yet.
        setError('');
      } else {
        setRejected(foldForCompare(trimmed));
        setError(userMessage(err, 'That recovery code was not accepted.'));
      }
      return failure;
    },
  };
}

/**
 * The code field, shown only once appcore has actually asked for one.
 *
 * It stays enabled on `EXPIRED` and `EXHAUSTED`, which looks wrong and is not: the
 * documented remedy for both is the operator re-running the (idempotent) reset, which
 * mints a FRESH code and resets the attempt count. A member still on the phone can
 * simply be read the new one and type it here. Disabling the field would send them
 * back to a dead screen for no reason.
 */
function RecoveryCodeField({ id, value, onChange, disabled }: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="mb-4">
      <label htmlFor={id} className="mb-1 block text-[12px] font-semibold text-navy font-sans">
        Recovery code
      </label>
      <input
        id={id}
        className="w-full px-3 py-2 text-[15px] tracking-[0.12em] border border-line-strong rounded-token box-border bg-surface text-navy font-sans outline-none focus:border-bronze-strong focus:ring-1 focus:ring-bronze-strong"
        value={value}
        onChange={e => onChange(e.target.value.slice(0, MAX_RECOVERY_CODE_LENGTH))}
        maxLength={MAX_RECOVERY_CODE_LENGTH}
        disabled={disabled}
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        placeholder="XXXXX-XXXXX"
      />
      <p className="mt-1.5 text-[11px] leading-relaxed text-slate font-sans">
        We read this to you on the call when we reset your sign-in keys. Capital letters
        and the dash do not matter, so type it however you wrote it down.
      </p>
    </div>
  );
}

/**
 * A first-guess name for a new passkey, from the coarse platform the browser
 * reports. Deliberately NOT derived from the authenticator's `aaguid`: a large
 * share of real authenticators report an all-zero aaguid, so it names nothing
 * for exactly the devices a user most needs to tell apart (appcore docs/29 F26).
 * This is only ever a prefilled default the user edits before enrolling, so a
 * coarse or slightly wrong guess costs nothing.
 */
function defaultPasskeyLabel(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android phone';
  if (/CrOS/i.test(ua)) return 'Chromebook';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows PC';
  if (/Linux/i.test(ua)) return 'Linux computer';
  return 'Passkey';
}

/**
 * `askForLabel` shows an editable name field before the ceremony. Settings turns
 * it on because that is where a user holds more than one passkey and an unnamed
 * list is unreadable; the first-run wall and onboarding leave it off so the
 * single-tap enrollment F1 worked to slim down stays a single tap.
 *
 * `hasExistingPasskey` only changes error copy. `webauthnRegisterOptions` sends
 * `excludeCredentials`, so re-enrolling from a device that already holds one of
 * this user's passkeys raises a flat `NotAllowedError` that is indistinguishable
 * from a genuine cancellation (browsers do not separate the two, on purpose). We
 * cannot know which happened, so when the user already has a passkey the copy
 * names both possibilities instead of asserting the wrong one.
 */
export function AddPasskeyForm({ token, buttonLabel, hint, askForLabel, hasExistingPasskey, onDone }: {
  token: string;
  buttonLabel: string;
  hint?: string;
  askForLabel?: boolean;
  hasExistingPasskey?: boolean;
  onDone: (stepUp: EnrollmentStepUp) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [label, setLabel] = useState(() => (askForLabel ? defaultPasskeyLabel() : ''));
  const recovery = useRecoveryCode();
  /**
   * The attestation from an attempt appcore refused for the recovery code alone, held so
   * the retry can redeem it. appcore checks the code BEFORE it consumes the single-use
   * challenge, so the response is still good, and reusing it means a member on the phone
   * with an operator touches their authenticator ONCE rather than once per code attempt.
   */
  const [pendingResponse, setPendingResponse] = useState<RegistrationResponseJSON | null>(null);
  // useId, not a literal: the codebase's own PromptModal does the same, and a
  // hardcoded id silently breaks the label/input association the moment two of
  // these are ever mounted together.
  const labelInputId = useId();

  async function addPasskey() {
    // A name made only of invisible characters is refused here, before the ceremony, with the
    // same plain sentence the name forms use (crossmarc/steward-app#2041). `trim()` does not
    // remove those characters, so it would otherwise reach the façade and come back as a
    // generic 400 after the user had already approved the passkey on their device.
    if (label.trim() && !hasVisibleContent(label.trim())) { setError(INVISIBLE_NAME_MESSAGE); return; }
    setBusy(true);
    setError('');
    // Captured, not read back from state after the await: `setPendingResponse` will not
    // have applied yet within this handler.
    let response = pendingResponse;
    try {
      if (!response) {
        const optionsJSON = await webauthnRegisterOptions(token);
        response = await startRegistration({ optionsJSON });
      }
      // An empty name is omitted rather than sent: appcore rejects a blank label
      // (min 1 after trim) and applies its own default when the field is absent. Same
      // for the code, which must be absent (not empty) unless the server asked.
      const trimmedLabel = label.trim();
      const result = await webauthnRegisterVerify(token, {
        response,
        ...(trimmedLabel ? { label: trimmedLabel } : {}),
        ...recovery.payload,
      });
      setPendingResponse(null);
      onDone({ stepUpMinted: result.stepUpMinted, assuranceLevel: result.assuranceLevel });
    } catch (err) {
      const recoveryFailure = recovery.classify(err, setError);
      if (recoveryFailure) {
        // 🔑 Keep the attestation for REQUIRED and INVALID, where the challenge genuinely
        // survives, and THROW IT AWAY for EXPIRED and EXHAUSTED, where it cannot. The
        // remedy for those two is the operator re-running the reset, and that cascade
        // deletes every `FACTORCHAL#` row this user has. Holding the response across it
        // would offer "Finish adding your passkey" over a challenge that is already gone,
        // so the member's next click would spend their fresh code and then 404.
        setPendingResponse(REMEDY_IS_A_FRESH_RESET.includes(recoveryFailure) ? null : response);
        return;
      }
      // Any other failure may have consumed the challenge (a bad attestation) or let it
      // lapse (5 minute TTL), so the held response is no longer redeemable and the next
      // attempt has to start a fresh ceremony.
      setPendingResponse(null);
      setError(
        err instanceof Error && err.name === 'NotAllowedError'
          ? (hasExistingPasskey
            ? 'That didn’t finish. If this device already holds one of your passkeys, add the new one from the other device or security key instead. Otherwise, try again when you’re ready.'
            : 'That was cancelled. Try again when you’re ready.')
          : userMessage(err, 'Couldn’t add your passkey. Try again.'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    // A form, not a bare div, so the recovery-code field below submits on Enter like
    // every other code entry in the app. No call site wraps these forms in one of its
    // own, so this cannot nest.
    <form onSubmit={e => { e.preventDefault(); void addPasskey(); }}>
      {askForLabel && (
        <div className="mb-4">
          <label htmlFor={labelInputId} className="mb-1 block text-[12px] font-semibold text-navy font-sans">
            Name this passkey
          </label>
          <input
            id={labelInputId}
            className={formCls.input}
            value={label}
            onChange={e => setLabel(e.target.value.slice(0, MAX_LABEL_LENGTH))}
            maxLength={MAX_LABEL_LENGTH}
            disabled={busy}
            autoComplete="off"
          />
          <p className="mt-1.5 text-[11px] text-slate font-sans">
            Something you’ll recognize later, like “iPhone” or “Work laptop”.
          </p>
        </div>
      )}
      {recovery.needed && (
        <>
          <p className="mb-3 text-[12px] leading-relaxed text-mid font-sans">
            {pendingResponse
              ? 'Your passkey is ready. One last step: because your sign-in keys were reset, we need the recovery code before we can add it to your account.'
              : 'Because your sign-in keys were reset, we need the recovery code. Enter it, then add your passkey again.'}
          </p>
          <RecoveryCodeField
            id={recovery.inputId}
            value={recovery.value}
            onChange={recovery.set}
            disabled={busy}
          />
        </>
      )}
      <button
        type="submit"
        disabled={busy || (recovery.needed && !recovery.submittable)}
        className="flex w-full items-center justify-center gap-2 px-5 py-3 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-60"
      >
        {!pendingResponse && <Fingerprint size={16} strokeWidth={ICON_STROKE} aria-hidden />}
        {pendingResponse
          ? (busy ? 'Checking…' : 'Finish adding your passkey')
          : (busy ? 'Waiting for your passkey…' : buttonLabel)}
      </button>
      {hint && !recovery.needed && <p className="mt-2 text-center text-[11px] text-slate font-sans">{hint}</p>}
      {error && <div className="mt-3 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger font-sans">{error}</div>}
    </form>
  );
}

/**
 * `askForLabel` inserts a naming step BEFORE the secret is minted, because the label
 * rides on the enrollment call and appcore returns the secret exactly once, so there
 * is no second chance to attach one. Settings turns it on (that is where a user
 * holds more than one authenticator and an unnamed list is unreadable); the
 * first-run wall and onboarding leave it off and keep minting on mount, so the
 * enrollment F1 worked to slim down is unchanged.
 */
export function AddTotpForm({ token, askForLabel, onDone }: {
  token: string;
  askForLabel?: boolean;
  onDone: (stepUp: EnrollmentStepUp) => void;
}) {
  const [minted, setMinted] = useState<{ factorId: string; otpauthUri: string; secret: string; qr: string } | null>(null);
  const [label, setLabel] = useState(askForLabel ? 'Authenticator app' : '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  /** Neutral, non-blaming copy for something the form did FOR the member (re-minting an
   *  expired secret). Separate from `error` so it is not styled as their mistake, and so it
   *  survives the re-mint that follows it. */
  const [notice, setNotice] = useState('');
  const recovery = useRecoveryCode();
  const labelInputId = useId();

  // Mint EXACTLY once — appcore returns the secret ONCE (no-store), so this must
  // never be re-fetched. Guarded against StrictMode's double-invoked effects, which
  // would otherwise mint two secrets and silently overwrite the first right as the
  // user might be scanning it. The same guard covers the naming step: a double
  // submit cannot mint twice either.
  const minting = useRef(false);

  /**
   * Throw away the pending secret and go back to the pre-mint step so a fresh one can be
   * minted.
   *
   * 🔑 This is what stops the QR screen becoming a dead end, and the reason it is needed is
   * upstream: appcore's reset cascade deletes EVERY `FACTOR#` row, pending TOTP rows
   * included. So the documented remedy for an expired or exhausted code (the operator
   * re-runs the reset) destroys the very secret on screen, and so does the plain 15 minute
   * pending TTL. Without this, activation answers `FACTOR_NOT_FOUND` forever, `minting`
   * stays latched, and only a page reload escapes.
   */
  function restartEnrollment(message: { error: string } | { notice: string }) {
    setMinted(null);
    minting.current = false;
    setCode('');
    // A `notice` survives the re-mint that follows it, because `startEnrollment` clears
    // `error` but not this. Without the split, an automatic re-mint would wipe its own
    // explanation and a new QR would appear with nothing saying why.
    if ('error' in message) { setError(message.error); setNotice(''); }
    else { setNotice(message.notice); setError(''); }
  }

  async function startEnrollment() {
    if (minting.current) return;
    // Same local refusal as the passkey form (crossmarc/steward-app#2041), before the secret
    // is minted: a minted secret is shown once, so a late refusal would cost the user a mint.
    if (label.trim() && !hasVisibleContent(label.trim())) { setError(INVISIBLE_NAME_MESSAGE); return; }
    minting.current = true;
    setBusy(true);
    setError('');
    try {
      const trimmedLabel = label.trim();
      const enrollment = await totpEnroll(token, {
        ...(trimmedLabel ? { label: trimmedLabel } : {}),
        ...recovery.payload,
      });
      const qr = await qrToDataURL(enrollment.otpauthUri, { margin: 1, width: 200 });
      setMinted({ ...enrollment, qr });
      // The code was accepted, so stop asking. It stays in state and still rides the
      // activation call, which is where appcore actually consumes it.
      recovery.accepted();
    } catch (err) {
      // Released so the user can correct the name (or supply the recovery code) and
      // retry. Safe because the throw means no secret was returned, so there is nothing
      // to overwrite. A refused enrollment also spends nothing: appcore answers
      // `RECOVERY_CODE_REQUIRED` without charging an attempt, which is what makes it
      // safe to probe here on mount.
      minting.current = false;
      // Any failure here means the notice is stale: it described a re-mint that has not
      // happened. Leaving it would caption a recovery prompt with "that setup timed out",
      // naming the wrong cause on a screen a reset produced.
      setNotice('');
      if (recovery.classify(err, setError)) return;
      setError(userMessage(err, 'Couldn’t start setup. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (askForLabel) return; // wait for the user to name it first
    void startEnrollment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function activate(e: React.FormEvent) {
    e.preventDefault();
    if (!minted || code.trim().length < 6) return;
    setBusy(true);
    setError('');
    setNotice('');
    // Set when the pending secret turned out to be dead for a reason that needs NOTHING
    // from the member, so a new one can simply be minted. Kicked off after the `finally`
    // below, so this handler's `setBusy(false)` cannot land on top of the new call's
    // `setBusy(true)` and leave the button idle mid-flight.
    let remintNow = false;
    try {
      const result = await totpActivate(token, {
        factorId: minted.factorId,
        code: code.trim(),
        ...recovery.payload,
      });
      onDone({ stepUpMinted: result.stepUpMinted, assuranceLevel: result.assuranceLevel });
    } catch (err) {
      const recoveryFailure = recovery.classify(err, setError);
      if (recoveryFailure) {
        // EVERY recovery failure at activation sends the member back to the start, not just
        // the two whose remedy is a fresh reset.
        //
        // Usually the pending row is already gone: the reset that placed or replaced the hold
        // deletes every FACTOR# row. The exception is the pure clock case, where the hold
        // predates this enrollment and the code's 45 minute window lapses while the 15 minute
        // pending row is still alive. Restarting is still right there, because the remedy is a
        // fresh reset and THAT deletes the row anyway, so a secret kept here would be dead by
        // the time a new code existed. Discard either way, for one reason or the other.
        //
        // `classify` has set the error copy; this adds the one thing it cannot know, which is
        // that setup has to begin again.
        restartEnrollment({
          error: recoveryFailure === 'RECOVERY_CODE_REQUIRED'
            ? 'Your sign-in keys were reset while you were setting this up, so we need to start again with the recovery code.'
            : `${userMessage(err, 'That recovery code was not accepted.')} Setting up again from the start.`,
        });
        // Never auto-remint here: appcore wants the code on the enroll call too, so the
        // member has to supply it at the pre-mint step first.
      } else if (isPendingEnrollmentGone(err)) {
        // The pending secret expired (15 minutes) or was cleared, so the QR on screen is
        // dead. Mint a new one rather than letting every retry re-post the same dead id.
        restartEnrollment({ notice: 'That setup timed out, so here is a fresh code to scan.' });
        // Nothing to collect from the member (no name step, no outstanding recovery code),
        // so re-mint straight away and put a live QR back on screen. Without this the
        // restart clears the dead secret and leaves them on a bare error with no way
        // forward, which is the same dead end by another route.
        remintNow = !askForLabel && !recovery.needed;
      } else {
        setError(userMessage(err, 'That code didn’t match. Check your app and try again.'));
        setCode('');
      }
    } finally {
      setBusy(false);
    }
    // `startEnrollment` cannot loop back here: it never raises FACTOR_NOT_FOUND, and any
    // other failure it hits stops at its own error handling.
    if (remintNow) void startEnrollment();
  }

  // Pre-mint step, for anything that has to be collected before the secret exists: the
  // name (`askForLabel`, Settings only) and the recovery code, once appcore has asked
  // for it. The wall and onboarding reach this ONLY under a recovery hold, where a
  // deliberate extra step is the entire point; with no hold the effect above has already
  // minted and nothing here renders.
  if (!minted && (askForLabel || recovery.needed)) {
    return (
      <form onSubmit={e => { e.preventDefault(); void startEnrollment(); }}>
        {/* The notice belongs here as well as on the QR screen. With `askForLabel` the
            auto-remint is suppressed (the name has to be collected first), so this is the
            only place a "that setup timed out" explanation can land. Without it the QR
            simply vanished and the member was returned to the naming step with no reason
            given. */}
        {notice && (
          <div className="mb-3 rounded-token border border-line bg-cream/50 px-3 py-2 text-[12px] text-mid font-sans" role="status">
            {notice}
          </div>
        )}
        {recovery.needed && (
          <p className="mb-3 text-[12px] leading-relaxed text-mid font-sans">
            Because your sign-in keys were reset, we need the recovery code before setting
            up a new authenticator app.
          </p>
        )}
        {askForLabel && (
          <div className="mb-4">
            <label htmlFor={labelInputId} className="mb-1 block text-[12px] font-semibold text-navy font-sans">
              Name this authenticator app
            </label>
            <input
              id={labelInputId}
              className={formCls.input}
              value={label}
              onChange={e => setLabel(e.target.value.slice(0, MAX_LABEL_LENGTH))}
              maxLength={MAX_LABEL_LENGTH}
              // Locked while the enroll is in flight. `startEnrollment` reads `label` from
              // its own closure, so an edit made mid-call would be silently dropped, and
              // because the secret is minted ONCE there is no second chance to attach a name.
              disabled={busy}
              autoComplete="off"
            />
            <p className="mt-1.5 text-[11px] text-slate font-sans">
              Something you’ll recognize later, like “1Password” or “Phone”.
            </p>
          </div>
        )}
        {recovery.needed && (
          <RecoveryCodeField
            id={recovery.inputId}
            value={recovery.value}
            onChange={recovery.set}
            disabled={busy}
          />
        )}
        {error && <div className="mt-3 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger font-sans">{error}</div>}
        <button
          type="submit"
          disabled={busy || (recovery.needed && !recovery.submittable)}
          className="mt-4 w-full px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? 'Setting up…' : 'Continue'}
        </button>
      </form>
    );
  }

  // No secret yet and nothing to collect. Either the first mint is still in flight, or it
  // failed. A failure needs a way out: this branch used to render a bare error with no
  // controls at all, so a re-mint that itself failed (an enroll-quota 429, a network blip)
  // left the member on a dead screen escapable only by reloading the page. That matters more
  // now, because a failed activation deliberately routes back through here.
  if (!minted) {
    return error
      ? (
        <div>
          <div className="rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger font-sans">{error}</div>
          <button
            type="button"
            onClick={() => void startEnrollment()}
            disabled={busy}
            className="mt-3 w-full px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-50"
          >
            {busy ? 'Setting up…' : 'Try again'}
          </button>
        </div>
      )
      : <div className="text-[12px] text-slate font-sans">Setting up…</div>;
  }

  return (
    <form onSubmit={e => void activate(e)}>
      {notice && (
        <div className="mb-3 rounded-token border border-line bg-cream/50 px-3 py-2 text-[12px] text-mid font-sans" role="status">
          {notice}
        </div>
      )}
      <ol className="m-0 mb-4 list-decimal space-y-3 pl-5 text-[12px] leading-relaxed text-mid font-sans">
        <li>
          Open your authenticator app (Google Authenticator, 1Password, Authy, or similar) and scan this code.
          <div className="mt-2 flex justify-center">
            <img src={minted.qr} width={160} height={160} alt="QR code for authenticator app setup" className="rounded-token border border-line" />
          </div>
          <details className="mt-2">
            <summary className="cursor-pointer text-[11px] text-slate">Can’t scan it? Enter this key by hand</summary>
            <code className="mt-1 block break-all rounded-token bg-cream/60 px-2 py-1.5 text-[11px] text-navy">{minted.secret}</code>
          </details>
        </li>
        <li>
          Enter the 6-digit code it shows.
          <input
            className="mt-2 w-full px-3 py-2 text-[16px] tracking-[0.3em] text-center border border-line-strong rounded-token box-border bg-surface text-navy font-sans outline-none focus:border-bronze-strong focus:ring-1 focus:ring-bronze-strong"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            maxLength={6}
          />
        </li>
      </ol>
      {/* NO recovery-code field here, deliberately. A recovery problem reported at
          ACTIVATION means this pending secret is already gone upstream (the reset that
          placed or replaced the hold deleted it), so a field on this screen would collect a
          fresh code and then 404 against a dead `factorId`. `activate` sends the member back
          to the pre-mint step instead, which is the only surface where a new code can
          actually be used. */}
      {error && <div className="mb-3 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger font-sans">{error}</div>}
      <button
        type="submit"
        disabled={busy || code.trim().length < 6}
        className="w-full px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? 'Verifying…' : 'Verify and finish'}
      </button>
    </form>
  );
}
